/**
 * AuCoverage — state-grouped index of every Australian university WUA
 * covers. Feeds `GET /api/coverage/au`.
 *
 * Mirrors `UkCoverage.jsx` in structure so the two coverage pages feel
 * like siblings, but colour-codes each Australian state (VIC=indigo,
 * NSW=sky, QLD=amber, WA=rose, SA=violet, ACT=emerald, TAS=teal, NT=orange)
 * and swaps the HESA badge for a QILT badge (Quality Indicators for
 * Learning and Teaching — the Australian Government's federal
 * Graduate Outcomes Survey).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowRight, Loader2, ShieldCheck, Award } from "lucide-react";

const STATE_ORDER = ["vic", "nsw", "qld", "wa", "sa", "act", "tas", "nt"];

const STATE_META = {
  vic:  { tint: "bg-indigo-50 border-indigo-200",  accent: "text-indigo-900",  dot: "bg-indigo-500" },
  nsw:  { tint: "bg-sky-50 border-sky-200",        accent: "text-sky-900",     dot: "bg-sky-500" },
  qld:  { tint: "bg-amber-50 border-amber-200",    accent: "text-amber-900",   dot: "bg-amber-500" },
  wa:   { tint: "bg-rose-50 border-rose-200",      accent: "text-rose-900",    dot: "bg-rose-500" },
  sa:   { tint: "bg-violet-50 border-violet-200",  accent: "text-violet-900",  dot: "bg-violet-500" },
  act:  { tint: "bg-emerald-50 border-emerald-200",accent: "text-emerald-900", dot: "bg-emerald-500" },
  tas:  { tint: "bg-teal-50 border-teal-200",      accent: "text-teal-900",    dot: "bg-teal-500" },
  nt:   { tint: "bg-orange-50 border-orange-200",  accent: "text-orange-900",  dot: "bg-orange-500" },
  unknown: { tint: "bg-slate-50 border-slate-200", accent: "text-slate-700",   dot: "bg-slate-400" },
};

function Stat({ label, value, testId }) {
  return (
    <div className="flex flex-col" data-testid={testId}>
      <span className="text-3xl font-serif tracking-tight text-slate-900">
        {value}
      </span>
      <span className="mt-0.5 text-[11px] uppercase tracking-wider text-slate-500">
        {label}
      </span>
    </div>
  );
}

function UniRow({ uni }) {
  return (
    <Link
      to={`/uni/${uni.id}`}
      className="group flex items-center justify-between gap-2 rounded border border-slate-200 bg-white px-2.5 py-2.5 sm:px-3 hover:border-slate-400 hover:bg-slate-50 transition-colors min-w-0 overflow-hidden"
      data-testid={`au-coverage-uni-${uni.id}`}
    >
      <div className="min-w-0 flex-1 overflow-hidden">
        <div className="flex items-center gap-1.5 min-w-0">
          {uni.world_rank ? (
            <span className="text-[10px] font-underground-sc text-slate-500 tabular-nums shrink-0">
              #{uni.world_rank}
            </span>
          ) : null}
          <span className="truncate text-sm font-medium text-slate-900 group-hover:text-slate-700 min-w-0">
            {uni.name}
          </span>
          {uni.has_qilt_salary ? (
            <span
              className="inline-flex items-center gap-1 rounded border border-emerald-200 bg-emerald-50 px-1 py-0.5 text-[9px] font-underground-sc tracking-wider text-emerald-800 shrink-0"
              title="This university carries a QILT-linked salary from the Australian Government's Graduate Outcomes Survey."
            >
              <ShieldCheck className="h-2.5 w-2.5" />
              QILT
            </span>
          ) : null}
        </div>
        <div className="mt-0.5 truncate text-[11px] text-slate-500">
          {uni.city}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <div className="text-right">
          <div className="text-sm font-mono tabular-nums text-slate-900 leading-tight">
            {uni.degree_count}
          </div>
          <div className="text-[9px] uppercase tracking-wider text-slate-400 leading-tight">
            {uni.degree_count === 1 ? "degree" : "degrees"}
          </div>
        </div>
        <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-slate-700 transition-colors" />
      </div>
    </Link>
  );
}

function StateBlock({ code, block }) {
  const meta = STATE_META[code] || STATE_META.unknown;
  if (!block.total_unis) return null;
  return (
    <section
      className={`rounded-lg border p-5 sm:p-6 ${meta.tint}`}
      data-testid={`au-coverage-state-${code}`}
    >
      <header className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2.5">
          <span className={`inline-block h-2.5 w-2.5 rounded-full ${meta.dot}`} />
          <h2 className={`text-lg font-editorial tracking-tight ${meta.accent}`}>
            {block.label}
          </h2>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] uppercase tracking-wider text-slate-600">
          <span>
            <span className="text-slate-900 font-mono tabular-nums">
              {block.total_unis}
            </span>{" "}
            unis
          </span>
          <span>
            <span className="text-slate-900 font-mono tabular-nums">
              {block.total_degrees}
            </span>{" "}
            degrees
          </span>
          <span title="Universities with a QILT-linked salary attached">
            <span className="text-emerald-800 font-mono tabular-nums">
              {block.qilt_covered_unis}
            </span>{" "}
            QILT
          </span>
        </div>
      </header>
      <div className="grid gap-2 sm:grid-cols-2">
        {block.unis.map((u) => (
          <UniRow key={u.id} uni={u} />
        ))}
      </div>
    </section>
  );
}

export default function AuCoverage() {
  const [state, setState] = useState({ loading: true, data: null, err: null });

  useEffect(() => {
    let live = true;
    api
      .get("/coverage/au")
      .then((r) => live && setState({ loading: false, data: r.data, err: null }))
      .catch((e) => live && setState({ loading: false, data: null, err: e?.message || "failed" }));
    return () => {
      live = false;
    };
  }, []);

  if (state.loading) {
    return (
      <div className="mx-auto max-w-6xl px-4 py-24 flex items-center justify-center text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin mr-2" />
        Loading Australian coverage…
      </div>
    );
  }
  if (state.err || !state.data) {
    return (
      <div className="mx-auto max-w-6xl px-4 py-24 text-slate-600">
        Couldn't load Australian coverage: {state.err || "empty response"}
      </div>
    );
  }
  const { states, totals } = state.data;

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:py-14" data-testid="au-coverage-page">
      <header className="mb-8 sm:mb-10">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-3">
          <Link to="/methodology/data-sources" className="hover:text-slate-800">
            Methodology
          </Link>
          {" · "}
          <span className="text-slate-900">Australia coverage</span>
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-editorial tracking-tight text-slate-900">
          Every Australian university, grouped by state.
        </h1>
        <p className="mt-4 max-w-3xl text-base text-slate-600">
          Not just the Group of Eight. This is every Australian university
          WhichUniversityAdvisor currently indexes — Victoria and NSW alongside
          Tasmania and the Northern Territory — with the honest depth of how
          many degrees we've published and which entries carry a QILT-linked
          salary (Quality Indicators for Learning and Teaching — the federal
          Graduate Outcomes Survey).
        </p>

        <div className="mt-6 grid grid-cols-3 gap-6 sm:gap-10">
          <Stat
            testId="au-coverage-total-unis"
            label="Universities indexed"
            value={totals.unis}
          />
          <Stat
            testId="au-coverage-total-degrees"
            label="Undergrad degrees"
            value={totals.degrees.toLocaleString()}
          />
          <Stat
            testId="au-coverage-total-qilt"
            label="With QILT-linked salary"
            value={totals.qilt_covered_unis}
          />
        </div>
      </header>

      <div className="space-y-6">
        {STATE_ORDER.map((code) =>
          states[code] ? <StateBlock key={code} code={code} block={states[code]} /> : null
        )}
        {states.unknown ? (
          <StateBlock code="unknown" block={states.unknown} />
        ) : null}
      </div>

      <footer className="mt-10 text-[12px] text-slate-500">
        <span className="inline-flex items-center gap-1.5">
          <Award className="h-3 w-3" />
          World ranks shown are WUA's world composite. Universities are sorted
          by WUA depth of coverage (degree count) — the entries we've published
          the most on appear first. Multi-campus unis (e.g. Deakin ·
          Melbourne + Geelong) resolve to their headquartered state.
        </span>
      </footer>
    </div>
  );
}
